'use client';

import React, { useCallback, useRef } from 'react';
import type {
  ContentAttributes,
  PopoverProps,
} from 'components/Popover/Popover';
import { isFunction } from 'utils/is-function';
import { Popover } from '../Popover';
import { MenuList, type MenuListProps } from './MenuList';

export type MenuProps = PopoverProps &
  Pick<
    MenuListProps,
    | 'onArrowKeyLeft'
    | 'onArrowKeyRight'
    /**
     * The `aria-describedby` attribute is used to indicate the element that provides a detailed, extended description for the menu.
     */
    | 'aria-describedby'
    /**
     * The `aria-details` attribute is used to indicate the element that provides a detailed, extended description for the menu.
     */
    | 'aria-details'
    /**
     * The `aria-label` attribute is used to define a string value that labels the menu.
     */
    | 'aria-label'
    /**
     * The `aria-labelledby` attribute is used to indicate the element that serves as the label for the menu.
     */
    | 'aria-labelledby'
  > & {
    minWidth?: string;
    maxWidth?: string;
    width?: string;
  };

/**
 * Menu
 *
 * A menu composes Popover, and shows a keyboard
 * navigable list of items which possibly have
 * submenus.
 */

export function Menu({
  minWidth,
  maxWidth,
  width,
  children,
  className,
  content,
  initialFocusRef,
  shouldAnimate = true,
  onArrowKeyLeft,
  onArrowKeyRight,
  'aria-describedby': ariaDescribedby,
  'aria-details': ariaDetails,
  'aria-labelledby': ariaLabelledby,
  'aria-label': ariaLabel,
  ...other
}: MenuProps) {
  const menuListRef = useRef(null);

  // encapsulate the content in a MenuList
  const renderContent = useCallback(
    (props: ContentAttributes) => (
      <MenuList
        ref={menuListRef}
        aria-describedby={ariaDescribedby}
        aria-details={ariaDetails}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledby}
        maxWidth={maxWidth}
        minWidth={minWidth}
        onArrowKeyLeft={onArrowKeyLeft}
        onArrowKeyRight={onArrowKeyRight}
        width={width}
      >
        {isFunction(content) ? content(props) : content}
      </MenuList>
    ),
    [
      ariaDescribedby,
      ariaDetails,
      ariaLabel,
      ariaLabelledby,
      maxWidth,
      minWidth,
      onArrowKeyLeft,
      onArrowKeyRight,
      width,
      content,
    ]
  );

  return (
    <Popover
      className={className}
      content={renderContent}
      // @ts-expect-error FIXME(strictNullChecks)
      initialFocusRef={initialFocusRef ?? menuListRef}
      shouldAnimate={shouldAnimate}
      {...other}
    >
      {children}
    </Popover>
  );
}
